// leadContext.tsx
import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { leadApi } from '@/services/api/leads';
import { formatInDate } from '@/utils/formatDate';
import attendantStorage from '@/utils/LocalStorage/attendant';
import { safeJsonParse } from '@/utils/safeJsonParse';

// types.ts
export interface Lead {
  id: number;
  nome: string;
  telefone: string;
  origem: string;
  stage: string;
  stage_id: string;
  description: string;
  attendantId: string;
  createdAt: string;
  Atendente?: any;
  attendantName?: string;
}

export const etapaJornadaOptions = [
  "Não classificado",
  "Em contato",
  "Perdido",
];

interface LeadContextType {
  leads: Lead[];
  fetchLeads: () => void;
  addLead: (lead: Lead) => void;
  addLeadCreated: (lead: any) => void;
  handleDelete: (leadId: number) => Promise<void>; // Adicionando deleteLead aqui
  loading: boolean;
  error: Error | null;
  reloadList: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<Error | null>>;
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  setReloadList: React.Dispatch<React.SetStateAction<boolean>>;
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  leadsMemory: Lead[];
  setLeadsMemory: React.Dispatch<React.SetStateAction<Lead[]>>;
  filterArchived : boolean;
  setFilterArchived : React.Dispatch<React.SetStateAction<boolean>>;
}

const LeadContext = createContext<LeadContextType | undefined>(undefined);

export const useLeads = () => {
  const context = useContext(LeadContext);
  if (!context) {
    throw new Error('useLeads must be used within a LeadProvider');
  }
  return context;
};

export const LeadProvider = ({ children }: { children: ReactNode }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadList, setReloadList] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>("");
  const [leadsMemory, setLeadsMemory] = useState<Lead[]>([]);
  const [filterArchived, setFilterArchived] = useState<boolean>(false);

  useEffect(() => {
    console.log("reloadList no contexto:", reloadList);
  }, [reloadList]);

  useEffect(() => {
    if (filter) {
      setLeads((prevLeads) => prevLeads.filter((lead) => lead.stage === filter));
    } else {
      fetchLeads();
    }
  }, [filter, filterArchived]);

  const fetchLeads = async () => {
    setLoading(true);
    setError(null);
    const storageAtendente = localStorage.getItem("attendant");
    const atendente = storageAtendente ? safeJsonParse(storageAtendente) : null;
    
    try {
      const response = await leadApi.get(filterArchived, atendente.id);
      const formattedLeads = response.data.map((lead: any) => ({
        ...lead,
        createdAt: (lead.criadoEm),
        attendantName: lead.Atendente ? lead.Atendente.nome : "",
        stage: lead.estagio,
      }));
      setLeadsMemory(formattedLeads);
      setLeads(formattedLeads);
    } catch (error) {
      setError(error as Error);
    } finally {
      setLoading(false);
    }
  };

  async function handleDelete(leadId: number) {
    if (leadId !== null) {
      try {
        const response = await leadApi.deleteLead(leadId);
        console.log(response);
        console.log(response.status);
        if (response.status === 200) {
          setReloadList(true);
        }
      } catch (error) {
        console.error("Erro ao deletar o lead:", error);
      }
    }
  }  


  function addLeadCreated(lead: any) {
    setLeads((prevLeads) => {
      const maxId = prevLeads.length > 0 ? Math.max(...prevLeads.map(l => l.id)) : 0;
      const attendantGet = attendantStorage.getToken()
      const newLead: Lead = {
        id: maxId + 1,  // Incrementar o ID baseado no último ID presente na lista
        nome: lead.nome ?? '',
        telefone: lead.telefone ?? '',
        origem: lead.origem ?? '',
        stage: lead.estagio ?? '',
        stage_id: lead.estagio_id ?? '',
        description: lead.descricao ?? '',
        attendantId: lead.atendenteId ?? '',
        createdAt: formatInDate(lead.criadoEm, true) ?? '',
        Atendente: lead.Atendente ?? null,
        attendantName: attendantGet.nome ? attendantGet.nome : 'Marisco',
      };
      return [...prevLeads, newLead];
    });
  }
  
  const addLead = (lead: Lead) => {
    setLeads((prevLeads) => [...prevLeads, lead]);
  };

  return (
    <LeadContext.Provider value={{ leads, fetchLeads, addLead, loading, error, setLoading, setError, addLeadCreated, setLeads, reloadList, setReloadList, handleDelete, filter, setFilter, leadsMemory, setLeadsMemory, filterArchived, setFilterArchived}}>
      {children}
    </LeadContext.Provider>
  );
};